"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { PageLoadError } from "@/components/ui/page-load-error";

/**
 * Root error boundary — catches render errors anywhere below the root layout
 * (pages AND nested layouts like `(site)`), replacing Next's unbranded default
 * "This page couldn't load" fallback with the Kraahl "we broke our toy" page.
 * (Errors in the root layout itself surface via `app/global-error.tsx`.)
 */
export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations("errors");

  useEffect(() => {
    // Surface for the browser console / observability; the boundary swallows it otherwise.
    console.error(error);
  }, [error]);

  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.assign("/feed");
  };

  return (
    <PageLoadError
      layout="fullscreen"
      title={t("crashTitle")}
      subtitle={t("crashSubtitle")}
      detail={process.env.NODE_ENV === "development" ? error.message || null : null}
      reloadLabel={t("reload")}
      backLabel={t("back")}
      onReload={reset}
      onBack={handleBack}
    />
  );
}
